import React from 'react';

interface BattleLegendPanelProps {
  onClose: () => void;
}

const swatch = (color: string) => (
  <div style={{ width: '28px', height: '18px', backgroundColor: color, border: '1px solid rgba(255,255,255,0.15)', borderRadius: '2px', flexShrink: 0 }} />
);

const Row: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
    {swatch(color)}
    <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '12px', color: '#d2c09a' }}>{label}</span>
  </div>
);

const BattleLegendPanel: React.FC<BattleLegendPanelProps> = ({ onClose }) => {
  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: '#0b1824',
      border: '1px solid #243344',
      borderTop: '2px solid #c49a2a',
      borderRadius: '4px 4px 6px 6px',
      boxShadow: '0 0 0 1px rgba(196,154,42,0.06), 0 24px 64px rgba(0,0,0,0.8)',
      padding: '18px 20px',
      zIndex: 2000,
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: '#243344 transparent',
      color: '#d2c09a',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #243344' }}>
        <h3 style={{ margin: 0, fontFamily: "'Cinzel', Georgia, serif", fontSize: '16px', fontWeight: 600, letterSpacing: '0.06em', color: '#e6b84a' }}>
          Tile Color Legend
        </h3>
        <span
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={e => e.key === 'Enter' && onClose()}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: '#8b2424', color: '#f0d8d8',
            border: '1px solid rgba(217,68,68,0.4)', borderRadius: '3px',
            padding: '4px 10px', fontFamily: "'Exo 2', sans-serif",
            fontWeight: 700, fontSize: '13px', cursor: 'pointer',
            transition: 'background 0.18s ease', userSelect: 'none',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#d94444')}
          onMouseLeave={e => (e.currentTarget.style.background = '#8b2424')}
        >
          ×
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Terrain Types */}
        <div>
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#485e70', marginBottom: '8px', paddingBottom: '5px', borderBottom: '1px solid #243344' }}>
            Terrain Types
          </div>
          <Row color="#90EE90" label="Normal / Grass" />
          <Row color="#228B22" label="Forest (+Evasion)" />
          <Row color="#8B7355" label="Mountain (+Defense)" />
          <Row color="#87CEEB" label="Water (Slow)" />
          <Row color="#4682B4" label="Deep Water (Impassable)" />
          <Row color="#F4A460" label="Sand" />
          <Row color="#556B2F" label="Swamp (Slow, -Evasion)" />
          <Row color="#A9A9A9" label="Road (Fast Movement)" />
          <Row color="#B0E0E6" label="Ice (Slippery, -Evasion)" />
          <Row color="#FF6347" label="Lava (Damage)" />
        </div>

        <div>
          {/* Hazards */}
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#485e70', marginBottom: '8px', paddingBottom: '5px', borderBottom: '1px solid #243344' }}>
            Hazards
          </div>
          <Row color="#FF4500" label="Fire" />
          <Row color="#9ACD32" label="Poison" />
          <Row color="#00CED1" label="Ice" />
          <Row color="#FFD700" label="Lightning" />
          <Row color="#ADFF2F" label="Acid" />
          <Row color="#8B4513" label="Thorns" />
          <Row color="#4B0082" label="Miasma" />

          {/* Overlays */}
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#485e70', marginTop: '14px', marginBottom: '8px', paddingBottom: '5px', borderBottom: '1px solid #243344' }}>
            Overlays
          </div>
          {[
            ['🟦', '#7a96aa', 'Blue', 'Valid Move'],
            ['🟥', '#d94444', 'Red', 'Valid Attack Target'],
            ['🟪', '#9370db', 'Purple', 'Skill Target'],
            ['🟨', '#c49a2a', 'Yellow', 'Skill Range'],
            ['🟧', '#e6763a', 'Orange', 'AOE Area'],
            ['⚔️', '#7a96aa', 'Gray', 'Weapon Range'],
          ].map(([icon, color, name, desc]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0' }}>
              <span style={{ fontSize: '14px' }}>{icon}</span>
              <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '12px', color: color as string, fontWeight: 700 }}>{name}:</span>
              <span style={{ fontFamily: "'Exo 2', sans-serif", fontSize: '12px', color: '#7a96aa' }}>{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BattleLegendPanel;
